import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";

import { T } from "../../constants";
import { useUi, cx } from "../../ui";
import { fmtDate } from "../../../../lib/misc";
import { useInstallation } from "../../../../components/InstallationContext";
import { Loading, LoadingError } from "../../../../components/LoadingFork";
import ModalCard from "../../components/ModalCard";
import Plate from "../../components/Plate";
import VipBadge from "../../components/VipBadge";
import { sanctionedPlateLabel } from "../../helpers";
import useExpectedArrivals from "../useExpectedArrivals";
import { useRowStyles } from "./boardRows";

const useStyles = makeStyles({
  notice: {
    borderRadius: 8,
    padding: "10px 12px",
    fontSize: 13,
    background: T.surface,
    color: T.mutedFg,
    marginBottom: 12,
  },
  sanctioned: { color: T.fg },
  small: { padding: "8px 12px", fontSize: 13 },
  list: {
    border: `1px solid ${T.borderSoft}`,
    borderRadius: 10,
    overflow: "hidden",
    marginTop: 10,
  },
  /* The row draws its own top border as a separator. On the first row that
     lands on the list's border and is clipped by its rounded corner, which
     reads as a row cut off at the top. */
  firstRow: { "&:first-child": { borderTop: "none" } },
  state: { padding: 14, fontSize: 13, color: T.mutedFg },
});

/*
 * Where the guest is staying, when they are due and what they booked - the
 * three things that tell one expected arrival from another at a glance. The
 * guest's own stated arrival wins over the entry time the guest card sells.
 */
function metaLine(booking, timeZoneName) {
  const due = booking.expectedArrival ?? booking.checkIn;

  return [
    booking.guest?.guestRoom ? `Room ${booking.guest.guestRoom}` : null,
    due ? `due ${fmtDate(due, timeZoneName, "HH:mm")}` : null,
    booking.rateCard?.name ?? booking.guestCard ?? null,
  ]
    .filter(Boolean)
    .join(" · ");
}

/**
 * The booking behind a car, found by hand.
 *
 * One list, two jobs. With a `session` the car is already on the forecourt and
 * the booking is attached to the session it arrived on; without one the desk is
 * starting an arrival the camera never read, and the session is opened against
 * the booking instead. Either way the picked booking is what the intake runs
 * on, so the search and the rows are the same.
 */
export default function MatchBookingModal({
  session = null,
  onClose,
  onPick,
  onWalkUp,
  onDismiss,
  busy,
}) {
  const s = useStyles();
  const r = useRowStyles();
  const ui = useUi();
  const { installation } = useInstallation() ?? {};

  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");

  /* Search hits the server (booking label = plate + guest + rate card), so it
     reaches guests beyond the pages already loaded. */
  useEffect(() => {
    const t = setTimeout(() => setSearch(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const { items, count, loading, loadingMore, error, hasMore, loadMore } =
    useExpectedArrivals({ search, includeReturns: true });

  const sanctioned = session && sanctionedPlateLabel(session);

  let rows;
  if (error) {
    rows = (
      <li className={s.state}>
        <LoadingError error={error} />
      </li>
    );
  } else if (loading && items.length === 0) {
    rows = (
      <li className={s.state}>
        <Loading />
      </li>
    );
  } else if (items.length === 0) {
    rows = <li className={s.state}>No expected arrival matches.</li>;
  } else {
    rows = items.map((booking) => (
      <li
        key={booking.id}
        data-row
        className={cx(r.row, s.firstRow)}
        role="button"
        tabIndex={0}
        onClick={() => !busy && onPick(booking)}
        onKeyDown={(e) => {
          if (e.key !== "Enter" && e.key !== " ") return;
          e.preventDefault();
          if (!busy) onPick(booking);
        }}
      >
        <Plate size={12}>{booking.plate}</Plate>

        <div className={r.main}>
          <div className={r.line}>
            <span className={r.name}>{booking.guestName}</span>
            {booking.vip && <VipBadge />}
          </div>
          <div className={r.meta}>
            {metaLine(booking, installation?.timeZoneName)}
          </div>
        </div>

        <button type="button" className={cx(ui.ghostBtn, r.tiny)} disabled={busy}>
          {session ? "Match" : "Select"}
        </button>
      </li>
    ));
  }

  return (
    <ModalCard
      title={session ? `Match ${session.plate}` : "New arrival"}
      subtitle={
        session
          ? "No booking carries this registration. Match an expected arrival, start a walk-up, or dismiss."
          : "A car at the desk the camera did not read. Pick the guest's booking, or start a walk-up."
      }
      onClose={onClose}
      width={620}
      footerLeft={
        session && onDismiss ? (
          <button
            type="button"
            className={cx(ui.mutedBtn, s.small)}
            onClick={onDismiss}
            disabled={busy}
            title="Uber / taxi / drop-off only - remove from valet queue"
          >
            Not valet
          </button>
        ) : (
          <button type="button" className={cx(ui.mutedBtn, s.small)} onClick={onClose}>
            Cancel
          </button>
        )
      }
      footerRight={
        <button
          type="button"
          className={cx(ui.primaryBtn, s.small)}
          onClick={onWalkUp}
          disabled={busy}
        >
          + Walk-up booking
        </button>
      }
    >
      {session &&
        (sanctioned ? (
          <div className={cx(s.notice, s.sanctioned)}>
            Sanctioned plate: {sanctioned}. Admit without a booking by starting a
            walk-up, or dismiss.
          </div>
        ) : (
          <div className={s.notice}>
            Unknown registration. Check the guest's name or room before starting a
            walk-up.
          </div>
        ))}

      <input
        autoFocus
        className={ui.field}
        placeholder="Search expected arrivals by plate or guest name…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        aria-label="Search expected arrivals"
      />

      <ul className={cx(r.list, s.list)}>
        {rows}

        {hasMore && (
          <li className={r.loadMore}>
            <button
              type="button"
              className={cx(ui.mutedBtn, s.small)}
              onClick={loadMore}
              disabled={loadingMore}
            >
              {loadingMore
                ? "Loading…"
                : `Load more · showing ${items.length} of ${count}`}
            </button>
          </li>
        )}
      </ul>
    </ModalCard>
  );
}
